import React, { useState, useEffect } from 'react';
import { Card, Divider, Skeleton, Tooltip, notification, message, Typography } from 'antd';
import RemoveConfirm from 'components/shared/RemoveConfirm';
import AddEditTaxExemption from 'components/TaxExemptionActions/AddEdit/';
import UserStore from 'stores/UserStore';
import UIStore from '@/stores/UIStore';
import ErrorRender from 'components/shared/ErrorRender';

import { ITaxExemptions } from 'types/types';

import { LinkOutlined } from '@ant-design/icons';
import Verify from '../../../components/Verify';

const { Paragraph } = Typography;

const TaxExemptions: React.FC = () => {
    const [data, setData] = useState<ITaxExemptions[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const loadData = async () => {
        setLoading(true);

        await UserStore.getTaxExemptions()
            .then(data => {
                setData(data?.results);
                setLoading(false);
            })
            .catch(reason => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={reason} />,
                });
                setLoading(false);
            });
    };

    useEffect(() => {
        UIStore.setSelectedKey(['taxes']);
        UIStore.setDocumentTitle('Tax Exemptions');

        if (UserStore.isVerified) {
            loadData();
        }
    }, []);

    const handleRemove = async (id: number) => {
        setData(data.filter(item => item.id !== id));

        await UserStore.removeTaxExemption(id)
            .then(() => {
                message.success(`Successfully deleted!!!`);
            })
            .catch(errors => {
                loadData();
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={errors} />,
                });
            });
    };

    if (!UserStore.isVerified) {
        return <Verify />;
    }

    return (
        <>
            <Paragraph className="py-3">
                Enter your tax exemptions here. Upon submission, they go through an approval process. Enter the State
                and upload your exemption file(s).
            </Paragraph>

            <Card title="Manage Tax Exemptions" bordered>
                <AddEditTaxExemption updateData={() => loadData()} />

                <Divider />

                {loading && <Skeleton paragraph={{ rows: 4 }} />}

                <div className="flex flex-wrap w-full">
                    {data.map(item => (
                        <div
                            className="flex flex-col p-0 mr-4 mb-4 rounded w-full"
                            style={{
                                border: '1px solid #595959',
                                minWidth: '300px',
                                maxWidth: '410px',
                                overflow: 'hidden',
                            }}
                            key={item.id}
                        >
                            <p
                                className="w-full text-center text-xl text-white"
                                style={{
                                    backgroundColor: item.verified ? '#87D068' : '#ffb703',
                                    lineHeight: '32px',
                                    margin: 0,
                                }}
                            >
                                {item.verified ? (
                                    'Reviewed'
                                ) : (
                                    <Tooltip title={'The changes made in this form need to be verified by our team'}>
                                        Pending Review
                                    </Tooltip>
                                )}
                            </p>
                            <div className="flex flex-col px-5 pt-3 pb-3" style={{ minHeight: '138px' }}>
                                <div className="flex flex-row items-stretch w-full flex-1">
                                    <div className="flex flex-col" style={{ flex: '1 1 0', minWidth: 0 }}>
                                        <span className="text-2xl text-black leading-tight">
                                            {item.states.join(', ')}
                                        </span>
                                        {item.files.map(itemFile => {
                                            return (
                                                <a
                                                    key={itemFile.id}
                                                    href={itemFile.file}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    download="Nda Form"
                                                    style={{
                                                        color: '#595959',
                                                        minWidth: 0,
                                                        padding: '4px 0',
                                                        textDecoration: 'underline',
                                                    }}
                                                    className="flex items-center text-base"
                                                >
                                                    <LinkOutlined
                                                        className="pr-1"
                                                        style={{ flexShrink: 0, fontSize: 12 }}
                                                    />
                                                    <span
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {itemFile.filename.toLowerCase()}
                                                    </span>
                                                </a>
                                            );
                                        })}
                                    </div>
                                    <div
                                        aria-hidden="true"
                                        style={{ backgroundColor: '#d9d9d9', margin: '0 16px', width: 1 }}
                                    />
                                    <div
                                        className="flex flex-col items-center justify-center text-center"
                                        style={{ flex: '0 0 150px' }}
                                    >
                                        <span className="text-sm leading-tight" style={{ color: '#8D8D8D' }}>
                                            PARTIAL DEDUCTION:
                                        </span>
                                        <span className="text-3xl leading-none" style={{ color: '#595959' }}>
                                            {item.sales_tax_deduction
                                                ? parseFloat(item.sales_tax_deduction).toFixed(2)
                                                : '0'}
                                            %
                                        </span>
                                    </div>
                                </div>

                                <div className="flex flex-row w-full justify-between items-center mt-4">
                                    <RemoveConfirm
                                        handleRemove={handleRemove}
                                        name={item.states.toString()}
                                        record={item}
                                    />
                                    <AddEditTaxExemption updateData={() => loadData()} record={item} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* <List
                    itemLayout="horizontal"
                    dataSource={data}
                    locale={{
                        emptyText: (
                            <Empty
                                className="flex flex-col justify-center items-center"
                                image={emptyTaxExemption}
                                description={<span style={{ color: '#000' }}>There is not any Tax Exemption yet.</span>}
                            />
                        ),
                    }}
                    renderItem={item => (

                    )}
                /> */}
            </Card>
        </>
    );
};

export default TaxExemptions;
